/**
 * @file Purchase Album Photos related helper functions.
 */

import store from 'store';
import get from 'lodash.get';

const getMatchedPhotosForPurchase = albumMembership => {
  if (!albumMembership) {
    return [];
  }

  // Filter out already purchased photos
  return albumMembership.matched_photos_extended.records.filter(
    matchedRecord =>
      !albumMembership.entitled_matched_photos.records.find(
        entitledPhoto => entitledPhoto.id === matchedRecord.photo.id,
      ),
  );
};

/**
 * Returns all matched photos that are available
 * for PhotoPricing / Print Packages purchasing. This includes
 * photos that have already been purchased.
 *
 * @param {AlbumMembership} albumMembership The album membership.
 * @returns {Array<PhotoRecords>} List of matched photos that can be purchased.
 */
const getMatchedPhotosForPhotoPricingPurchase = albumMembership => {
  if (!get(albumMembership, 'matched_photos_extended')) {
    return [];
  }

  // Filter out already purchased photos
  return albumMembership.matched_photos_extended.records;
};

/**
 * Takes matched photos returned from an album's membership and merges
 * photos that contain more than one identity along with all faceboxes and
 * identity info.
 *
 * @param {Object} albumMembership - The album membership.
 * @returns {Array}
 */
const getMergedMatchedPhotosForPurchase = albumMembership => {
  if (!albumMembership) {
    return [];
  }

  const photos = getMatchedPhotosForPurchase(albumMembership);
  const cache = {};

  return (
    photos
      .map(record => {
        const cached = cache[record.photo.id];

        if (!cached) {
          cache[record.photo.id] = {
            ...record,
            comparedFaces: [record.compared_face.facebox],
            identities: [record.identity],
          };

          // Delete the original `compared_face` and `identity` references as
          // they might create confusion
          delete cache[record.photo.id].compared_face;
          delete cache[record.photo.id].identity;
        } else {
          cache[record.photo.id].comparedFaces.push(
            record.compared_face.facebox,
          );
          cache[record.photo.id].identities.push(record.identity);
        }

        return cached ? null : cache[record.photo.id];
      })
      // Filter out all falsey records
      .filter(Boolean)
  );
};

/**
 * Takes matched photos returned from an album's membership and merges
 * photos that contain more than one identity along with all faceboxes and
 * identity info.
 *
 * @param {Object} albumMembership - The album membership.
 * @returns {Array}
 */
const getMergedMatchedPhotosForPhotoPricingPurchase = albumMembership => {
  if (!albumMembership) {
    return [];
  }

  const photos = getMatchedPhotosForPhotoPricingPurchase(albumMembership);
  const cache = {};

  return (
    photos
      .map(record => {
        const cached = cache[record.photo.id];

        if (!cached) {
          cache[record.photo.id] = {
            ...record,
            comparedFaces: [record.compared_face.facebox],
            identities: [record.identity],
          };

          // Delete the original `compared_face` and `identity` references as
          // they might create confusion
          delete cache[record.photo.id].compared_face;
          delete cache[record.photo.id].identity;
        } else {
          cache[record.photo.id].comparedFaces.push(
            record.compared_face.facebox,
          );
          cache[record.photo.id].identities.push(record.identity);
        }

        return cached ? null : cache[record.photo.id];
      })
      // Filter out all falsey records
      .filter(Boolean)
  );
};

/**
 * Takes matched photos returned from an identity and merges
 * photos that contain more than one identity along with all faceboxes and
 * identity info.
 *
 * @param {SelectedIdentity} selectedIdentity
 * @returns {Array}
 */
const getIdentityMergedMatchedPhotos = selectedIdentity => {
  if (!selectedIdentity) {
    return [];
  }

  const photos = get(selectedIdentity, 'matched_photos_extended.records', []);
  const cache = {};

  return (
    photos
      .map(record => {
        const cached = cache[record.photo.id];
        if (!cached) {
          cache[record.photo.id] = {
            ...record,
            comparedFaces: [record.compared_face.facebox],
            identities: [record.identity],
          };

          // Delete the original `compared_face` and `identity` references as
          // they might create confusion
          delete cache[record.photo.id].compared_face;
          delete cache[record.photo.id].identity;
        } else {
          cache[record.photo.id].comparedFaces.push(
            record.compared_face.facebox,
          );
          cache[record.photo.id].identities.push(record.identity);
        }

        return cached ? null : cache[record.photo.id];
      })
      // Filter out all falsey records
      .filter(Boolean)
  );
};

/**
 * Figures out if the account has new un-purchased (or un-entitled) photos.
 *
 * @param {number} totalMatches - Total number of matches as returned from album_membership.matched_photos.
 * @param {number} totalEntitled - Total number of entitled photos as returned from album_membership.entitled_photos.
 * @returns {boolean}
 */
const hasPurchasablePhotos = (totalMatches, totalEntitled) => {
  return totalMatches > totalEntitled;
};

const getPpdPrice = album => {
  return !album.is_demo ? parseInt(album.default_price_per_photo, 10) : 0;
};

const getTotalPrice = (selectedPhotos, album) => {
  return selectedPhotos.length * getPpdPrice(album);
};

/**
 * Key for the help tooltip displayed on obfuscated photo lists.
 *
 * @returns {undefined}
 */
const getPpdViewerHelperKey = () => 'ppd_viewer_helper_hidden';

/**
 * Set PPD Photo Viewer helper status to store.
 *
 * @returns {undefined}
 */
const setPpdViewerHelperHiddenOnStore = () => {
  store.set(getPpdViewerHelperKey(), true);
};

/**
 * Get PPD Photo Viewer helper status from store.
 *
 * @returns {boolean}
 */
const getPpdViewerHelperStatusFromStore = () => {
  return store.get(getPpdViewerHelperKey());
};

/**
 * Given the array of selected identities, extract the
 * entitled photos from each of them so that we get
 * a finalized object with records & total.
 *
 * This will also filter the photos and make sure there
 * are no repeated ones.
 *
 * @param {Array<SelectedIdentity>} selectedIdentities - Identities from whom the photos will be extracted.
 * @param {Array<SelectedIdentity>} hiddenIdentities - Identities that will be ignored for extraction.
 * @returns {Object}
 */
const getEntitledPhotosFromSelectedIdentities = (
  selectedIdentities = [],
  hiddenIdentities = [],
) => {
  const hiddenIdentitiesIds = hiddenIdentities.map(({ id }) => id);
  const filteredIdentities = selectedIdentities.filter(
    selectedIdentity => !hiddenIdentitiesIds.includes(selectedIdentity.id),
  );

  return filteredIdentities.reduce(
    (photos, selectedIdentity) => {
      // Identities might appear in shared photos.
      // We must ensure that we filter unique ones.
      const photoIds = photos.records.map(({ id }) => id);
      const filteredPhotos = selectedIdentity.entitled_matched_photos.records.filter(
        photo => !photoIds.includes(photo.id),
      );

      return {
        total: photos.total + filteredPhotos.length,
        records: [...photos.records, ...filteredPhotos],
      };
    },
    {
      total: 0,
      records: [],
    },
  );
};

/**
 * Figures out if the account has new un-purchased (or un-entitled)
 * photos for any of the selected identities.
 *
 * @param {Array} selectedIdentities - The selected identities.
 * @returns {boolean}
 */
const hasPurchasablePhotosPerIdentity = selectedIdentities => {
  for (let i = 0; i < selectedIdentities.length; i += 1) {
    const selectedIdentity = selectedIdentities[i];
    if (
      selectedIdentity.matched_photos.total >
      selectedIdentity.entitled_matched_photos.total
    ) {
      return true;
    }
  }

  return false;
};

export {
  getMatchedPhotosForPurchase,
  getMergedMatchedPhotosForPurchase,
  getPpdPrice,
  getTotalPrice,
  setPpdViewerHelperHiddenOnStore,
  getPpdViewerHelperStatusFromStore,
  hasPurchasablePhotos,
  getEntitledPhotosFromSelectedIdentities,
  hasPurchasablePhotosPerIdentity,
  getMatchedPhotosForPhotoPricingPurchase,
  getMergedMatchedPhotosForPhotoPricingPurchase,
  getIdentityMergedMatchedPhotos,
};
